import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { CsvCell } from './dashboardExport';

// Lets the dashboard download every widget's data at once. Each widget registers how to produce
// its own table (title + CSV rows); the page asks them all, in layout order, when the download
// is clicked rather than holding a copy of every result.

export type WidgetCsvSection = { title: string; rows: CsvCell[][] };
type Producer = () => WidgetCsvSection | null;

type ContextValue = {
    register: (widgetId: string, producer: Producer) => () => void;
    collect: (order: string[]) => WidgetCsvSection[];
};

const DashboardExportContext = createContext<ContextValue | null>(null);

export const DashboardExportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const producers = useRef(new Map<string, Producer>());
    const value = useMemo<ContextValue>(
        () => ({
            register: (widgetId, producer) => {
                producers.current.set(widgetId, producer);
                return () => {
                    if (producers.current.get(widgetId) === producer) {
                        producers.current.delete(widgetId);
                    }
                };
            },
            collect: order =>
                order
                    .map(id => producers.current.get(id)?.() ?? null)
                    .filter((s): s is WidgetCsvSection => s != null),
        }),
        [],
    );
    return (
        <DashboardExportContext.Provider value={value}>{children}</DashboardExportContext.Provider>
    );
};

// The widget side. The producer is read through a ref, so registering once is enough even
// though it closes over the latest rows.
export const useRegisterWidgetExport = (widgetId: string, producer: Producer): void => {
    const ctx = useContext(DashboardExportContext);
    const latest = useRef(producer);
    latest.current = producer;
    useEffect(() => ctx?.register(widgetId, () => latest.current()), [ctx, widgetId]);
};

export const useDashboardExport = () => useContext(DashboardExportContext);

// Every widget's table one after another, each under its title and separated by a blank line.
export const combineCsvSections = (sections: WidgetCsvSection[]): CsvCell[][] =>
    sections.flatMap((section, i) => [...(i > 0 ? [[]] : []), [section.title], ...section.rows]);
